/**
 * Accounts tab pane — shows a compact list of registered MT5 accounts with
 * on/off toggle, balance/equity/PnL/positions summary, and the MetaAPI
 * billing estimator at the top of the pane (contextual to the accounts
 * currently deployed).
 *
 * Both AccountsPane and its child AccountRow live in this file because they
 * are only ever used together.
 */
import {
  HandMetal, Trash2, Loader2, Wifi, WifiOff, XCircle,
} from 'lucide-react';
import MetaApiBillingEstimator from './MetaApiBillingEstimator';
import { fmtMoney } from './tradingHelpers';

function AccountRow({ acc, isAr, onDelete, connectError, onConnect, onDisconnect, isToggling, positionsSummary }) {
  const pnlColor = (acc.profit || 0) >= 0 ? 'text-emerald-400' : 'text-red-400';
  const connected = !!acc.connected;
  const posTotal = positionsSummary?.total ?? 0;
  const posBuys = positionsSummary?.buys ?? 0;
  const posSells = positionsSummary?.sells ?? 0;
  // Prefer the live per-account position-PnL when we have it (it excludes any
  // stale historical data still lingering on `acc.profit`).
  const livePnL = positionsSummary?.profit;
  const effectivePnL = livePnL != null ? livePnL : acc.profit;
  const effectivePnlColor = (effectivePnL || 0) >= 0 ? 'text-emerald-400' : 'text-red-400';
  return (
    <div
      className={`rounded-xl border transition ${
        connected
          ? 'bg-emerald-500/[0.05] border-emerald-500/25'
          : connectError
            ? 'bg-red-500/[0.04] border-red-500/25'
            : 'bg-white/[0.02] border-white/10'
      }`}
      data-testid={`account-row-${acc.id}`}
    >
      <div className="grid grid-cols-12 items-center gap-3 px-3 py-2.5">
        {/* Explicit ON/OFF button — big, labelled, clearly clickable. */}
        <div className="col-span-2 flex justify-center">
          <button
            onClick={() => (connected ? onDisconnect?.(acc) : onConnect?.(acc))}
            disabled={isToggling}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border font-black text-[11px] tracking-wide transition ${
              isToggling
                ? 'bg-amber-500/20 border-amber-500/40 text-amber-200 cursor-wait'
                : connected
                  ? 'bg-emerald-500/25 border-emerald-500/60 text-emerald-200 hover:bg-emerald-500/35 shadow-[0_0_12px_rgba(16,185,129,0.25)]'
                  : 'bg-white/[0.03] border-white/20 text-white/60 hover:bg-white/[0.10] hover:text-white'
            }`}
            data-testid={`account-toggle-${acc.id}`}
            title={
              isToggling
                ? (isAr ? 'جاري الاتصال…' : 'Connecting…')
                : connected
                  ? (isAr ? 'اضغط لإيقاف هذا الحساب' : 'Click to stop this account')
                  : (isAr ? 'اضغط لتشغيل هذا الحساب' : 'Click to start this account')
            }
          >
            {isToggling
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : connected
                ? <Wifi className="h-3.5 w-3.5" />
                : <WifiOff className="h-3.5 w-3.5" />}
            <span>
              {isToggling
                ? (isAr ? 'اتصال…' : 'Connecting')
                : connected
                  ? (isAr ? 'تشغيل' : 'ON')
                  : (isAr ? 'إيقاف' : 'OFF')}
            </span>
            {connected && !isToggling && (
              <span
                className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"
                aria-hidden
              />
            )}
          </button>
        </div>
        <div className="col-span-2">
          <div className="text-sm font-bold text-white truncate">{acc.label}</div>
          <div className="text-[10px] text-white/50 truncate">{acc.login} · {acc.server}</div>
        </div>
        <div className="col-span-2 text-right">
          <div className="text-[10px] text-white/40">{isAr ? 'الرصيد' : 'Balance'}</div>
          <div className="text-sm font-bold">{fmtMoney(acc.balance, acc.currency || 'USD')}</div>
        </div>
        <div className="col-span-2 text-right">
          <div className="text-[10px] text-white/40">{isAr ? 'الإيكويتي' : 'Equity'}</div>
          <div className="text-sm">{fmtMoney(acc.equity, acc.currency || 'USD')}</div>
        </div>
        {/* Live open-positions summary — added per user request so each
            account row shows HOW MANY trades are live and their split. */}
        <div className="col-span-1 text-right" data-testid={`account-positions-${acc.id}`}>
          <div className="text-[10px] text-white/40">{isAr ? 'صفقات' : 'Open'}</div>
          {posTotal > 0 ? (
            <div className="text-sm font-bold text-white leading-tight">
              {posTotal}
              <div className="text-[9px] font-mono text-white/50">
                <span className="text-emerald-300">{posBuys}B</span>
                <span className="text-white/30">/</span>
                <span className="text-red-300">{posSells}S</span>
              </div>
            </div>
          ) : (
            <div className="text-sm text-white/30">—</div>
          )}
        </div>
        <div className="col-span-2 text-right">
          <div className="text-[10px] text-white/40">P&amp;L</div>
          <div
            className={`text-sm font-bold ${effectivePnL != null ? effectivePnlColor : pnlColor}`}
            data-testid={`account-pnl-${acc.id}`}
          >
            {effectivePnL != null ? (effectivePnL >= 0 ? '+' : '') + fmtMoney(effectivePnL, acc.currency || 'USD') : '—'}
          </div>
        </div>
        <div className="col-span-1 flex justify-end">
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition"
            data-testid={`delete-${acc.id}`}
            title={isAr ? 'حذف' : 'Delete'}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {connectError && (
        <div className="border-t border-red-500/20 px-3 py-2 flex items-start gap-2 text-[11px]">
          <XCircle className="h-3.5 w-3.5 text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-red-300 font-bold mb-0.5">
              {isAr ? 'سبب فشل الاتصال:' : 'Connect error:'}
            </div>
            <div className="text-red-200 font-mono break-all">{connectError}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AccountsPane({
  loading, accounts, isAr, onDelete,
  connectErrors = [], onClearErrors,
  onConnect, onDisconnect, togglingIds,
  positionsByAccount = {}, connectedCount = 0,
}) {
  const errorsByAcc = Object.fromEntries((connectErrors || []).map((e) => [e.account_id, e.error]));
  const toggling = togglingIds || new Set();
  return (
    <div className="space-y-3">
      {/* MetaAPI billing estimator — lives inside the Accounts tab (contextual
          to the "accounts you have deployed"). Was previously floating above
          the tabbed section — moved here per user request. */}
      <MetaApiBillingEstimator
        deployedCount={connectedCount}
        totalCount={accounts.length}
        isAr={isAr}
      />

      <div className="flex items-center justify-between">
        <span className="text-xs text-white/50">
          {isAr
            ? `${accounts.length} حساب مسجّل — اضغط زر التشغيل لكل حساب على حدة`
            : `${accounts.length} registered · click the ON/OFF button per account`}
        </span>
      </div>

      {/* Connection errors banner — persists so admin can read/copy */}
      {connectErrors.length > 0 && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/[0.06] p-3" data-testid="connect-errors">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-red-300 text-xs font-bold">
              <XCircle className="h-4 w-4" />
              {isAr ? `فشل اتصال ${connectErrors.length} حساب — الأسباب:` : `${connectErrors.length} account(s) failed — reasons:`}
            </div>
            <button
              onClick={onClearErrors}
              className="text-[10px] text-white/40 hover:text-white/80"
              data-testid="clear-connect-errors"
            >
              {isAr ? 'إخفاء' : 'Dismiss'}
            </button>
          </div>
          <ul className="space-y-1 text-[11px] text-red-200">
            {connectErrors.map((e, i) => {
              const acc = accounts.find((a) => a.id === e.account_id);
              return (
                <li key={i} className="font-mono">
                  <span className="text-white/70 font-bold">{acc?.label || e.account_id}:</span>{' '}
                  <span className="text-red-300">{e.error}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-white/40">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-12">
          <HandMetal className="h-10 w-10 text-white/20 mx-auto mb-3" />
          <p className="text-sm text-white/50 mb-1">
            {isAr ? 'لا توجد حسابات بعد' : 'No accounts yet'}
          </p>
          <p className="text-xs text-white/30">
            {isAr ? 'أضف حتى 6 حسابات على نفس البروكر بنفس رأس المال' : 'Add up to 6 accounts on the same broker'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {accounts.map((a) => (
            <AccountRow
              key={a.id}
              acc={a}
              isAr={isAr}
              onDelete={() => onDelete(a)}
              connectError={errorsByAcc[a.id]}
              onConnect={onConnect}
              onDisconnect={onDisconnect}
              isToggling={toggling.has(a.id)}
              positionsSummary={positionsByAccount[a.id]}
            />
          ))}
        </div>
      )}
    </div>
  );
}
